import * as THREE from "three";

const _v = new THREE.Vector3();
const _ndc = new THREE.Vector3();

/**
 * Given a raycast hit, find the nearest mesh vertex within `screenPx` pixels.
 * Returns the snapped world position, or the original hit point if no vertex is close enough.
 */
export function snapToVertex(
  hit: THREE.Intersection,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  screenPx = 12,
): THREE.Vector3 {
  const mesh = hit.object as THREE.Mesh;
  if (!mesh.geometry) return hit.point.clone();

  const pos = mesh.geometry.attributes.position;
  if (!pos) return hit.point.clone();

  // Convert snap radius from pixels to NDC units
  const ndcRadius = (screenPx / canvas.clientWidth) * 2;

  // Project hit point to screen
  const hitNDC = hit.point.clone().project(camera);

  let bestDist = ndcRadius;
  let best: THREE.Vector3 | null = null;

  for (let i = 0; i < pos.count; i++) {
    _v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
    _ndc.copy(_v).project(camera);

    // Only consider vertices that are roughly in front of camera
    if (_ndc.z > 1) continue;

    const dx = _ndc.x - hitNDC.x;
    const dy = _ndc.y - hitNDC.y;
    const d = Math.sqrt(dx * dx + dy * dy);

    if (d < bestDist) {
      bestDist = d;
      best = _v.clone();
    }
  }

  return best ?? hit.point.clone();
}
